import { useEffect, useRef } from 'react'
import { useLanguage } from '@/hooks/useLanguage'
import { useMe } from './useMe'
import { useWriteProfileLanguage } from './useWriteProfileLanguage'

/**
 * Единственное место, которое пишет язык интерфейса в профиль (`PATCH
 * /users/me`) — на КАЖДОЕ расхождение языка интерфейса с `me.languageCode`,
 * не только один раз за сессию. LanguageToggle сам ничего не пишет (общий
 * слой про auth не знает) — любое ручное переключение проходит через этот же
 * эффект, поэтому автор не может уйти от сверки, поменяв язык вручную.
 *
 * Направление именно такое: у веб-авторов в languageCode почти всегда 'en'
 * (так их заводит союзный сервис), поэтому чтение профиля в UI показало бы
 * русскому автору английский интерфейс без видимой причины.
 *
 * Живёт отдельным компонентом, а не внутри LanguageProvider: провайдер в
 * App.tsx стоит ВЫШЕ AuthProvider и про токен ничего не знает — зависимость
 * от useMe развернула бы её в неправильную сторону.
 */
export const ProfileLanguageSync = () => {
  const { language } = useLanguage()
  const { data: me } = useMe()
  const writeProfileLanguage = useWriteProfileLanguage()
  // Защита от повторов: помнит последнюю ПОПЫТКУ (пара «id пользователя +
  // язык»), не результат. Один и тот же расходящийся пейр не повторяется на
  // ре-рендерах — включая ре-рендер с упавшим patchMe (см.
  // useWriteProfileLanguage: ошибка глотается намеренно, интерфейс уже
  // переключился) — иначе упавший запрос уходил бы на каждый ре-рендер, пока
  // profile не совпадёт. Смена пользователя (другой id) снова открывает
  // попытку, даже если язык тот же — булев guard остался бы защёлкнутым
  // навсегда после первого пользователя сессии.
  const lastAttempt = useRef<{ userId: number; lang: string } | null>(null)

  useEffect(() => {
    if (!me) return
    if (me.languageCode === language) return
    const attempted = lastAttempt.current
    if (attempted && attempted.userId === me.id && attempted.lang === language) return
    lastAttempt.current = { userId: me.id, lang: language }
    writeProfileLanguage(language)
  }, [me, language, writeProfileLanguage])

  return null
}
